import React from "react";
import { motion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { Target, Eye, Award, GraduationCap, School, Sparkles, Quote, CheckCircle2 } from "lucide-react";
import heroBuilding from "../assets/hero-building.png";

const ABOUT_IMG = heroBuilding;

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="Who We Are"
        title={<>A legacy of <span className="italic text-gold-gradient">compassionate</span> care</>}
        subtitle="Founded in 1996, the A.R.S. Memorial Educational Society has built a legacy of quality education across Bokaro Steel City — and SVS College of Nursing proudly carries that mission forward into healthcare."
        image={ABOUT_IMG}
      />

      {/* LEGACY OF EXCELLENCE — A.R.S. Memorial Educational Society */}
      <section className="bg-navy relative overflow-hidden py-24 lg:py-28" data-testid="legacy-section">
        <div className="grain-overlay" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <span className="text-xs tracking-[0.3em] uppercase text-gold font-mono">A.R.S. Memorial Educational Society · Estd. 1996</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-sand mt-4 max-w-3xl leading-tight">
              Our legacy of <span className="italic text-gold-gradient">excellence</span>
            </h2>
            <p className="text-sand/70 mt-5 max-w-2xl leading-relaxed">
              SVS College of Nursing is proudly part of the A.R.S. Memorial Educational Society —
              building futures through quality education, innovation and social commitment.
            </p>
          </Reveal>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 mt-14 rounded-[2px] overflow-hidden">
            {[
              { n: "1996", l: "Founded" },
              { n: "4", l: "Institutions" },
              { n: "CBSE", l: "School Affiliation" },
              { n: "NCTE", l: "College Recognition" },
              { n: "1,800+", l: "Students Trained" },
              { n: "20,000", l: "Campus Area (Sq.M)" },
            ].map((s, i) => (
              <Reveal key={s.l} delay={i * 0.06}>
                <div className="bg-navy p-8 h-full" data-testid={`legacy-stat-${i}`}>
                  <div className="font-serif text-3xl sm:text-4xl text-gold-gradient">{s.n}</div>
                  <div className="mt-2 text-xs tracking-[0.15em] uppercase text-sand/60">{s.l}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ASSOCIATED INSTITUTIONS */}
      <section className="bg-sand py-24" data-testid="institutions-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">The Group</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4">Our associated institutions</h2>
            <p className="text-navy/60 mt-5 max-w-2xl leading-relaxed">
              A family of institutions in the Bokaro region delivering value-based education from
              preschool to teacher training.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {[
              {
                icon: School,
                name: "A.R.S. Public School",
                meta: "Est. 1996 · CBSE up to +2 · Co-Educational",
                desc: "Science & Commerce streams on a 20,000 sq.m campus with smart & digital classrooms, science laboratories, a rich library and playground.",
              },
              {
                icon: GraduationCap,
                name: "A.R.S. B.Ed. College",
                meta: "Est. 2012 · NCTE Recognized · BBMKU, Dhanbad",
                desc: "B.Ed. (100 seats) and D.El.Ed. (50 seats) teacher-training programmes, backed by specialized psychology, education-technology and language labs.",
              },
              {
                icon: Sparkles,
                name: "Aarambh Preschool — The Beginning",
                meta: "Est. 2023 · Preschool · Bokaro, Jharkhand",
                desc: "A premium play-based early childhood centre nurturing children's emotional, cognitive, social and physical growth through discovery.",
              },
            ].map((inst, i) => (
              <Reveal key={inst.name} delay={i * 0.1}>
                <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }} className="bg-white border border-navy/10 rounded-[2px] p-8 h-full" data-testid={`institution-${i}`}>
                  <inst.icon size={30} className="text-gold mb-6" strokeWidth={1.4} />
                  <h3 className="font-serif text-xl text-navy">{inst.name}</h3>
                  <p className="text-xs text-gold mt-1 font-medium">{inst.meta}</p>
                  <p className="text-navy/60 text-sm mt-4 leading-relaxed">{inst.desc}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT SOCIETY + DIRECTOR'S VISION */}
      <section className="bg-white py-24" data-testid="society-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">About the Society</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-navy mt-4 mb-6">A.R.S. Memorial Educational Society</h2>
            <p className="text-navy/70 leading-relaxed mb-6">
              A.R.S. Memorial Educational Society is a charitable educational society established in
              1996 with the objective of promoting quality education and the holistic development of
              students. It is the managing society of all the institutions located in Bokaro Steel
              City, Jharkhand.
            </p>
            <ul className="space-y-3">
              {[
                ["Name", "A.R.S. Memorial Educational Society"],
                ["Established", "1996"],
                ["Location", "Bokaro Steel City, Jharkhand, India"],
                ["Nature", "Educational and charitable society"],
              ].map(([k, v]) => (
                <li key={k} className="flex gap-3 text-navy/70">
                  <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" />
                  <span><span className="font-semibold text-navy">{k}:</span> {v}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Excellence", "Ethics", "Service", "Innovation"].map((t) => (
                <span key={t} className="text-xs tracking-[0.15em] uppercase text-navy/60 border border-navy/15 rounded-full px-4 py-1.5">{t}</span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative bg-navy text-sand rounded-[2px] p-8 sm:p-10 h-full overflow-hidden" data-testid="director-vision">
              <div className="grain-overlay" />
              <div className="relative">
                <Quote size={40} className="text-gold/50" />
                <span className="block text-xs tracking-[0.3em] uppercase text-gold font-mono mt-6">Director's Vision</span>
                <p className="text-sand/85 leading-relaxed mt-4 text-lg font-serif italic">
                  “As Director, my vision is to create an institution where excellence, compassion and
                  integrity shape every future nurse. I aspire to empower students with quality
                  education, practical skills, ethical values and confidence — enabling them to serve
                  society with dedication, embrace lifelong learning and become leaders in the
                  healthcare profession.”
                </p>
                <div className="mt-8 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-gold/15 ring-1 ring-gold/40 flex items-center justify-center font-serif text-gold text-lg">RP</div>
                  <div>
                    <div className="font-serif text-lg text-sand">R. L. Prasad Roy</div>
                    <div className="text-xs tracking-[0.2em] uppercase text-gold">Director</div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2px] aspect-[4/5] shadow-[0_20px_60px_rgba(10,30,77,0.2)]">
              <img
                src="https://customer-assets-agu9un31.emergentagent.net/job_health-ed-platform/artifacts/rwuobu4g_WhatsApp%20Image%202026-07-22%20at%204.18.56%20PM%20%281%29.jpeg"
                alt="SVS College of Nursing reception"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-gold/30" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Our Story</span>
              <h2 className="font-serif text-4xl sm:text-5xl text-navy mt-4 mb-6 leading-tight">
                Shaping the future of nursing
              </h2>
              <p className="text-navy/70 leading-relaxed mb-4">
                Located in Bahadurpur, Jainamore, Bokaro, SVS College of Nursing offers comprehensive
                ANM, GNM and B.Sc Nursing programs designed to build both clinical competence and
                compassionate character.
              </p>
              <p className="text-navy/70 leading-relaxed">
                Through a combination of rigorous theoretical instruction, modern simulation
                facilities and extensive hospital clinical postings, our students graduate ready to
                serve communities with confidence and integrity.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-8">
          {[
            { icon: Eye, t: "Vision", d: "To be a premier centre of nursing excellence, shaping globally competent and ethical healthcare professionals." },
            { icon: Target, t: "Mission", d: "To deliver excellence in nursing education and nurture skilled, compassionate and competent professionals." },
            { icon: Award, t: "Values", d: "Compassion, integrity and lifelong learning, cultivated through mentorship and service to community." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }} className="border border-navy/10 rounded-[2px] p-8 h-full bg-sand">
                <c.icon size={32} className="text-gold mb-6" strokeWidth={1.4} />
                <h3 className="font-serif text-2xl text-navy mb-3">{c.t}</h3>
                <p className="text-navy/60 leading-relaxed text-sm">{c.d}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
